import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import { Crown, Check, CreditCard, Loader2 } from 'lucide-react';
import { getBillingRole } from '../utils/billingRole';

const SubscriptionPlans = () => {
  const { API, user, setUser, language } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const content = {
    en: {
      chooseYourPlan: 'Annual Subscription',
      startFreeTrial: 'One clear yearly plan. Cancel renewal anytime and keep access through your paid-through date.',
      monthly: 'Monthly',
      annual: 'Annual',
      save: 'Save',
      perMonth: '/month',
      perYear: '/year',
      billedMonthly: 'Billed monthly',
      billedAnnually: 'Billed annually',
      subscribe: 'Subscribe',
      processing: 'Processing...',
      trialTitle: 'Annual membership',
      trialDesc: 'Billed once per year:',
      featuresIncluded: 'Features included:',
      feature1: 'Unlimited job applications',
      feature2: 'Profile verification badge',
      feature3: 'AI-powered job matching',
      feature4: 'Priority support',
      feature5: 'Advanced analytics',
      declineSubscription: 'Decline my subscription',
      termsNotice: 'By subscribing, you agree to our Terms of Service and Privacy Policy. Your subscription will automatically renew unless you cancel before the end of your trial period. Cancel anytime from your account settings.',
      declinedWarning: 'Subscription declined. You can browse but cannot bid, post jobs, or chat until you subscribe.',
      limitedFeatures: 'You can browse but some features are limited until you subscribe'
    },
    de: {
      chooseYourPlan: 'Jahresabonnement',
      startFreeTrial: 'Ein klarer Jahresplan. Sie können die Verlängerung jederzeit kündigen und behalten den Zugang bis zum bezahlten Enddatum.',
      monthly: 'Monatlich',
      annual: 'Jährlich',
      save: 'Sparen',
      perMonth: '/Monat',
      perYear: '/Jahr',
      billedMonthly: 'Monatlich abgerechnet',
      billedAnnually: 'Jährlich abgerechnet',
      subscribe: 'Abonnieren',
      processing: 'Verarbeitung...',
      trialTitle: 'Jahresmitgliedschaft',
      trialDesc: 'Einmal jährlich abgerechnet:',
      featuresIncluded: 'Enthaltene Funktionen:',
      feature1: 'Unbegrenzte Jobbewerbungen',
      feature2: 'Profilverifizierungsabzeichen',
      feature3: 'KI-gestütztes Job-Matching',
      feature4: 'Prioritäts-Support',
      feature5: 'Erweiterte Analysen',
      declineSubscription: 'Abonnement ablehnen',
      termsNotice: 'Mit dem Abonnement stimmen Sie unseren AGB und Datenschutzrichtlinien zu. Ihr Abonnement verlängert sich automatisch, es sei denn, Sie kündigen vor Ende des Testzeitraums. Jederzeit in Ihren Kontoeinstellungen kündbar.',
      declinedWarning: 'Abonnement abgelehnt. Sie können browsen, aber nicht bieten, Jobs posten oder chatten, bis Sie abonnieren.',
      limitedFeatures: 'Sie können browsen, aber einige Funktionen sind eingeschränkt, bis Sie abonnieren'
    },
    it: {
      chooseYourPlan: 'Abbonamento annuale',
      startFreeTrial: 'Un unico piano annuale. Puoi annullare il rinnovo e mantenere l’accesso fino alla data pagata.',
      monthly: 'Mensile',
      annual: 'Annuale',
      save: 'Risparmia',
      perMonth: '/mese',
      perYear: '/anno',
      billedMonthly: 'Fatturato mensilmente',
      billedAnnually: 'Fatturato annualmente',
      subscribe: 'Abbonati',
      processing: 'Elaborazione...',
      trialTitle: 'Abbonamento annuale',
      trialDesc: 'Fatturato una volta all’anno:',
      featuresIncluded: 'Funzionalità incluse:',
      feature1: 'Candidature illimitate',
      feature2: 'Badge di verifica profilo',
      feature3: 'Matching ordini con IA',
      feature4: 'Supporto prioritario',
      feature5: 'Analisi avanzate',
      declineSubscription: 'Rifiuta l\'abbonamento',
      termsNotice: 'Abbonandoti, accetti i nostri Termini di Servizio e l\'Informativa sulla Privacy. Il tuo abbonamento si rinnoverà automaticamente a meno che non annulli prima della fine del periodo di prova. Annulla in qualsiasi momento dalle impostazioni del tuo account.',
      declinedWarning: 'Abbonamento rifiutato. Puoi navigare ma non puoi fare offerte, pubblicare lavori o chattare fino a quando non ti abboni.',
      limitedFeatures: 'Puoi navigare ma alcune funzionalità sono limitate fino a quando non ti abboni'
    },
    fr: {
      chooseYourPlan: 'Abonnement annuel',
      startFreeTrial: 'Un abonnement annuel clair. Annulez le renouvellement et gardez l’accès jusqu’à la date payée.',
      monthly: 'Mensuel',
      annual: 'Annuel',
      save: 'Économisez',
      perMonth: '/mois',
      perYear: '/an',
      billedMonthly: 'Facturé mensuellement',
      billedAnnually: 'Facturé annuellement',
      subscribe: 'S\'abonner',
      processing: 'Traitement...',
      trialTitle: 'Abonnement annuel',
      trialDesc: 'Facturé une fois par an :',
      featuresIncluded: 'Fonctionnalités incluses:',
      feature1: 'Candidatures illimitées',
      feature2: 'Badge de vérification du profil',
      feature3: 'Matching d\'emplois par IA',
      feature4: 'Support prioritaire',
      feature5: 'Analyses avancées',
      declineSubscription: 'Refuser mon abonnement',
      termsNotice: 'En vous abonnant, vous acceptez nos Conditions d\'Utilisation et notre Politique de Confidentialité. Votre abonnement se renouvellera automatiquement sauf si vous annulez avant la fin de votre période d\'essai. Annulez à tout moment depuis les paramètres de votre compte.',
      declinedWarning: 'Abonnement refusé. Vous pouvez naviguer mais ne pouvez pas enchérir, publier des emplois ou discuter jusqu\'à ce que vous vous abonniez.',
      limitedFeatures: 'Vous pouvez naviguer mais certaines fonctionnalités sont limitées jusqu\'à ce que vous vous abonniez'
    }
  };

  const t = content[language] || content.en;
  const isClientBilling = getBillingRole(user) !== 'professional';

  const plans = {
    yearly: {
      name: t.annual,
      price: isClientBilling ? 12 : 99,
      priceId: 'yearly',
      period: t.perYear,
      pricePerMonth: isClientBilling ? '1.00' : '8.25',
      savings: null,
      description: t.billedAnnually,
      recommended: true,
    }
  };

  const selectedPlan = 'yearly';

  const features = [
    t.feature1,
    t.feature2,
    t.feature3,
    t.feature4,
    t.feature5,
  ];

  const handleSkip = async () => {
    // Decline subscription - user can keep account but cannot use premium features
    try {
      // Call backend to mark subscription as declined
      const token = localStorage.getItem('helply_token');
      await axios.post(`${API}/subscriptions/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.warning(t.declinedWarning);
      
      if (isClientBilling) {
        navigate('/dashboard/client');
      } else {
        navigate('/dashboard/professional');
      }
    } catch (error) {
      console.error('Error declining subscription:', error);
      // Still navigate but show info
      toast.info(t.limitedFeatures);
      
      if (isClientBilling) {
        navigate('/dashboard/client');
      } else {
        navigate('/dashboard/professional');
      }
    }
  };

  const handleSubscribe = () => {
    navigate(`/subscription-payment${isClientBilling ? '' : '?billing_role=professional'}`, { state: { plan: selectedPlan } });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">{t.chooseYourPlan}</h1>
          <p className="text-lg text-gray-600">{t.startFreeTrial}</p>
        </div>

        {/* Yearly Plan Badge */}
        <div className="flex justify-center mb-6">
          <div className="bg-blue-50 border border-blue-200 rounded-full px-6 py-2 inline-flex items-center gap-2">
            <Crown className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-blue-700">
              {t.annual} — {isClientBilling ? 'CHF 12' : 'CHF 99'}/year
            </span>
          </div>
        </div>

        {/* Selected Plan Card */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-blue-500 overflow-hidden mb-6 relative">
          {plans[selectedPlan].recommended && (
            <div className="absolute top-4 right-4">
              <Crown className="w-8 h-8 text-yellow-500 fill-yellow-500" />
            </div>
          )}

          <div className="p-8">
            {/* Plan Name & Price */}
            <div className="text-center mb-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <h2 className="text-2xl font-bold text-gray-900">{plans[selectedPlan].name}</h2>
              </div>
              
              <div className="flex items-baseline justify-center gap-1">
                <span className="text-5xl font-bold text-gray-900">{plans[selectedPlan].price} CHF</span>
                <span className="text-xl text-gray-600">{plans[selectedPlan].period}</span>
              </div>
              
              {plans[selectedPlan].pricePerMonth && (
                <p className="text-sm text-blue-600 mt-2">
                  ({plans[selectedPlan].pricePerMonth} CHF{t.perMonth})
                </p>
              )}
              
              <p className="text-sm text-gray-500 mt-2">{plans[selectedPlan].description}</p>
            </div>

            {/* Subscribe Button */}
            <button
              onClick={handleSubscribe}
              disabled={loading}
              data-testid="subscription-plans-subscribe-button"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 rounded-lg transition-colors mb-6 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t.processing}
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  {isClientBilling ? `${t.subscribe} — CHF 12${t.perYear}` : `${t.subscribe} — CHF 99${t.perYear}`}
                </>
              )}
            </button>

            {/* Trial Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-900 text-center">
                <strong>{t.trialTitle}</strong><br />
                {t.trialDesc} {plans[selectedPlan].price} CHF{plans[selectedPlan].period}
              </p>
            </div>

            {/* Features */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-4">{t.featuresIncluded}</h3>
              <ul className="space-y-3">
                {features.map((feature, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Skip Button - More prominent for verified professionals */}
        <div className="text-center">
          <button
            onClick={handleSkip}
            data-testid="decline-subscription-plan-button"
            className="text-gray-600 hover:text-gray-900 font-medium underline"
          >
            {t.declineSubscription}
          </button>
        </div>

        {/* Fine Print */}
        <div className="mt-8 text-center text-xs text-gray-500 max-w-2xl mx-auto">
          <p>
            {t.termsNotice}
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPlans;
